import React, { FC, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    selectImageLayout,
    selectSelectedText,
    selectTemporaryTexts,
    selectImageProject,
} from '../../../../store/selectors/imageSelectors';
import {
    moveText,
    setSelectedText,
    updateText,
    deleteText,
    setTemporaryTexts,
} from '../../../../store/slices/imageSlice';
import { useAppDispatch } from '../../../../store/store';
import { useCanvasContainer } from '../../../../shared/context/CanvasContainerContext';
import s from './TextContainer.module.css';
import IconDelete from '../../../../shared/ui/icons/IconDelete';
import IconMove from '../../../../shared/ui/icons/IconMove';
import IconRotate from '../../../../shared/ui/icons/IconRotate';
import {
    selectActiveEditorMode,
    selectActivePrompt,
} from '../../../../store/selectors/pageSelectors';
import { InlineText, TextProps } from '../../../../shared/types/imagesType';
import { canvasColorToCss, rgbaToCss } from '../prompt-blocks/AddTextBlock';
import { updateImageLayout } from '../../../../shared/api/imageLayoutApi';
import isEqual from 'lodash/isEqual';
import { setSelectedImage } from '../../../../store/slices/imageSlice';
interface RotateData {
    initialMouseAngle: number;
    initialRotation: number;
}

const TextContainer: FC = () => {
    const containerRef = useCanvasContainer();
    const currentText = useSelector(selectSelectedText);
    const dispatch = useAppDispatch();
    const activePrompt = useSelector(selectActivePrompt);
    const texts = useSelector(selectImageLayout)?.inline_text;
    const temporaryTexts = useSelector(selectTemporaryTexts);
    const imageLayout = useSelector(selectImageLayout);
    const imageData = useSelector(selectImageProject);
    const activeEditorMode = useSelector(selectActiveEditorMode);

    useEffect(() => {
        console.log('texts', texts);
        if (texts && texts.length > 0) {
            console.log('texts', texts);
            dispatch(setTemporaryTexts(texts));
        }
    }, [activeEditorMode]);

    const textRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const editingTextareaRef = useRef<HTMLTextAreaElement | null>(null);

    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const moved = useRef(false);
    const rotateDataRef = useRef<RotateData | null>(null);

    const handleDragStart = (e: React.MouseEvent, text: InlineText) => {
        if ((e.target as HTMLElement).tagName.toLowerCase() === 'textarea') return;
        if (!containerRef.current) return;
        e.stopPropagation();
        dispatch(setSelectedText(text));
        const rect = containerRef.current.getBoundingClientRect();
        setDraggingId(text.id);
        setDragOffset({
            x: e.clientX - (rect.x + text.location.x),
            y: e.clientY - (rect.y + text.location.y),
        });
        moved.current = false;
    };

    const handleDragMove = (e: React.MouseEvent) => {
        if (draggingId === null || !containerRef.current) return;
        moved.current = true;
        const rect = containerRef.current.getBoundingClientRect();
        let newX = e.clientX - dragOffset.x - rect.x;
        let newY = e.clientY - dragOffset.y - rect.y;

        dispatch(moveText({ id: draggingId, x: newX, y: newY }));
    };

    const handleDragEnd = () => {
        setDraggingId(null);
    };

    const handleChangeText = (newContent: string) => {
        if (currentText) {
            dispatch(updateText({ ...currentText, content: newContent }));
        }
    };

    const handleRotateMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!containerRef.current || !currentText) return;
        const rect = containerRef.current.getBoundingClientRect();
        const cx = rect.x + currentText.location.x;
        const cy = rect.y + currentText.location.y;
        const angle = Math.atan2(e.clientY - cy, e.clientX - cx);
        rotateDataRef.current = { initialMouseAngle: angle, initialRotation: currentText.rotation };
        window.addEventListener('mousemove', handleRotateMouseMove as EventListener);
        window.addEventListener('mouseup', handleRotateMouseUp);
    };

    const handleRotateMouseMove = (e: MouseEvent) => {
        const data = rotateDataRef.current;
        if (!data || !containerRef.current || !currentText) return;
        const rect = containerRef.current.getBoundingClientRect();
        const cx = rect.x + currentText.location.x;
        const cy = rect.y + currentText.location.y;
        const angle = Math.atan2(e.clientY - cy, e.clientX - cx);
        const delta = (angle - data.initialMouseAngle) * (180 / Math.PI);
        dispatch(
            updateText({ ...currentText, rotation: Math.round(data.initialRotation + delta) }),
        );
    };

    const handleRotateMouseUp = () => {
        rotateDataRef.current = null;
        window.removeEventListener('mousemove', handleRotateMouseMove as EventListener);
        window.removeEventListener('mouseup', handleRotateMouseUp);
    };

    const handleDelete = async () => {
        if (currentText) {
            dispatch(deleteText(currentText.id));
            handleUpldateInlineText([]);
        }
    };

    const handleMoveMouseDown = (e: React.MouseEvent, text: InlineText) => {
        e.stopPropagation();
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        setDraggingId(text.id);
        setDragOffset({
            x: e.clientX - (rect.x + text.location.x),
            y: e.clientY - (rect.y + text.location.y),
        });
        moved.current = false;
    };

    useEffect(() => {
        if (activePrompt !== 'add_text') {
            dispatch(setSelectedText(null));
        }
    }, [activePrompt]);

    const handleUpldateInlineText = (text: InlineText[]) => {
        if (currentText) {
            if (imageLayout && imageData) {
                dispatch(setSelectedText(null));
                if (isEqual(imageLayout.inline_text, text)) {
                    return;
                }

                const newImageLayout = {
                    ...imageLayout,
                    inline_text: text,
                };
                dispatch(
                    updateImageLayout({
                        imageProjectId: imageData.id,
                        layoutId: imageLayout.id,
                        layout: newImageLayout,
                    }),
                );
            }
        }
    };

    return (
        <div
            className={s.container}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            style={{ pointerEvents: activePrompt === 'add_text' ? 'auto' : 'none' }}
            onClick={() => handleUpldateInlineText(temporaryTexts)}
        >
            {temporaryTexts?.map((text) => (
                <div
                    key={text.id}
                    ref={(el) => {
                        textRefs.current[text.id] = el;
                    }}
                    className={s.block_container}
                    style={{
                        left: text.location.x,
                        top: text.location.y,
                        transform: `translate(-50%, -50%) rotate(${text.rotation}deg)`,
                    }}
                    onMouseDown={(e) => handleDragStart(e, text)}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (moved.current) {
                            moved.current = false;
                            return;
                        }
                        // dispatch(setSelectedText(text));
                        // dispatch(setSelectedImage({ image: null, layoutId: null }));
                    }}
                >
                    {currentText?.id === text.id && (
                        <div>
                            <button
                                onMouseDown={(e) => handleMoveMouseDown(e, text)}
                                className={s.button}
                            >
                                <IconMove color="white" width={15} />
                            </button>
                            <button onMouseDown={handleRotateMouseDown} className={s.button}>
                                <IconRotate color="white" width={15} />
                            </button>
                            <button onClick={handleDelete} className={s.button}>
                                <IconDelete color="white" width={15} />
                            </button>
                        </div>
                    )}
                    <div
                        className={`${s.text_container} ${currentText?.id === text.id ? s.active : ''}`}
                        style={{
                            textShadow: text.text_shadow_color
                                ? `${text.text_shadow_offset_x}px ${text.text_shadow_offset_y}px ${text.text_shadow_blur}px ${rgbaToCss(text.text_shadow_color)}`
                                : undefined,
                            backgroundColor: rgbaToCss(text.background_color),
                            borderRadius: text.background_radius,
                            padding: `${text.background_height}px ${text.background_width}px`,
                        }}
                    >
                        {currentText?.id === text.id ? (
                            <textarea
                                ref={editingTextareaRef}
                                value={text.content}
                                onChange={(e) => {
                                    handleChangeText(e.target.value);
                                }}
                                autoFocus
                                className={s.textarea}
                                style={{
                                    fontSize: text.size,
                                    color: canvasColorToCss(text.color),
                                    fontFamily: text.font,
                                    WebkitTextStroke: `${text.stroke}px ${rgbaToCss(text.stroke_color)}`,
                                    textShadow: text.text_shadow_color
                                        ? `${text.text_shadow_offset_x}px ${text.text_shadow_offset_y}px ${text.text_shadow_blur}px ${rgbaToCss(text.text_shadow_color)}`
                                        : undefined,
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    fontSize: text.size,
                                    color: canvasColorToCss(text.color),
                                    fontFamily: text.font,
                                    whiteSpace: 'pre-wrap',
                                    width: '100%',
                                    height: '100%',
                                    WebkitTextStroke: `${text.stroke}px ${rgbaToCss(text.stroke_color)}`,
                                    WebkitTextFillColor: canvasColorToCss(text.color),
                                }}
                            >
                                {text.content}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default TextContainer;
